import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, Search, Heart, Bell, User } from "lucide-react";
import { useAuth } from "../lib/auth";
import useSavedSearchUnread from "../lib/useSavedSearchUnread";

/**
 * Fixed-bottom mobile navigation. Hidden on desktop (md:hidden).
 * Five anchors: Home, Search, Saved searches, Favorites, Profile.
 * The Saved-searches icon displays a count badge mirroring the header pill
 * so mobile users see incoming matches without scrolling up.
 */
export default function MobileBottomNav() {
    const { user } = useAuth();
    const location = useLocation();
    const { pathname } = location;
    const { t } = useTranslation();
    const savedSearchUnread = useSavedSearchUnread();

    // Authenticated regular users (b2c / b2b) see the saved-searches counter.
    const showCounter = (
        user && user !== false &&
        user.role !== "admin" &&
        typeof savedSearchUnread === "number" && savedSearchUnread > 0
    );

    // Parse the `?tab=` query so each Dashboard tab can highlight its own
    // bottom-nav slot instead of all of them lighting up on /dashboard.
    const activeTab = (new URLSearchParams(location.search)).get("tab");

    const isItemActive = (item) => {
        const targetPath = (item.to.split("?")[0]) || "/";
        if (targetPath === "/") {
            return pathname === "/";
        }
        if (item.tab) {
            // Dashboard tab item: only active when both the path AND the
            // `?tab=` query match exactly. The bare /dashboard profile slot
            // wins when no tab query is present.
            return pathname.startsWith(targetPath) && activeTab === item.tab;
        }
        if (targetPath === "/dashboard") {
            // Profile slot — active only when on /dashboard *without* an
            // explicit tab query (i.e. the default tab).
            return pathname.startsWith("/dashboard") && !activeTab;
        }
        return pathname.startsWith(targetPath);
    };

    const items = [
        { to: "/", icon: Home, label: t("mobile_nav.home"), testid: "mob-nav-home" },
        { to: "/search", icon: Search, label: t("mobile_nav.search"), testid: "mob-nav-search" },
        {
            to: "/dashboard?tab=saved-searches",
            tab: "saved-searches",
            icon: Bell,
            label: t("mobile_nav.saved"),
            testid: "mob-nav-saved",
            badge: showCounter ? savedSearchUnread : null,
        },
        {
            to: "/dashboard?tab=favorites",
            tab: "favorites",
            icon: Heart,
            label: t("mobile_nav.favorites"),
            testid: "mob-nav-fav",
        },
        {
            to: user ? "/dashboard" : "/login",
            icon: User,
            label: user ? t("mobile_nav.profile") : t("nav.login"),
            testid: "mob-nav-profile",
        },
    ];

    return (
        <nav
            className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 grid grid-cols-5"
            data-testid="mobile-bottom-nav"
            aria-label={t("mobile_nav.aria")}
        >
            {items.map((item) => {
                const { to, icon: Icon, label, testid, badge } = item;
                const active = isItemActive(item);
                return (
                    <Link
                        key={testid}
                        to={to}
                        data-testid={testid}
                        className={`relative flex flex-col items-center justify-center gap-0.5 py-2 transition-colors ${
                            active ? "text-[#111827]" : "text-gray-500 hover:text-[#111827]"
                        }`}
                    >
                        <span className="relative">
                            <Icon className="w-5 h-5" strokeWidth={active ? 2.4 : 2} />
                            {typeof badge === "number" && badge > 0 && (
                                <span
                                    className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center font-mono-num"
                                    data-testid="mob-nav-saved-badge"
                                    aria-label={t("saved_search_pill.aria", { count: badge })}
                                >
                                    {badge > 99 ? "99+" : badge}
                                </span>
                            )}
                        </span>
                        <span className={`text-[10px] uppercase tracking-wider ${active ? "font-bold" : "font-medium"}`}>
                            {label}
                        </span>
                    </Link>
                );
            })}
        </nav>
    );
}
